
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function FloodlightInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Floodlight Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional floodlight installation in Fresno, CA. High-output LED floodlights for security, sports courts, and commercial properties.',
    url: `${siteUrl}/services/lighting-outdoor/floodlight-installation`,
  };

  const features = [
    {
      icon: 'ri-flashlight-fill',
      title: 'High-Output LED Floods',
      description: 'Bright, energy-efficient lighting for large outdoor areas',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Security Floodlights',
      description: 'Deter intruders with powerful motion-activated lighting',
    },
    {
      icon: 'ri-basketball-line',
      title: 'Sports Court Lighting',
      description: 'Professional-grade lighting for backyard courts and fields',
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Floodlights',
      description: 'Illuminate building facades, signage, and parking areas',
    },
    {
      icon: 'ri-run-line',
      title: 'Motion Sensor Options',
      description: 'Automatic activation for security and energy savings',
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Adjustable Positioning',
      description: 'Aim and focus light exactly where needed',
    },
  ];

  return (
    <>
      <SEOHead
        title="Floodlight Installation Fresno CA | LED Security Floodlights"
        description="Professional floodlight installation in Fresno, CA. High-output LED floodlights for security, sports courts, and commercial properties. Licensed electricians."
        canonical="/services/lighting-outdoor/floodlight-installation"
        keywords="floodlight installation Fresno, LED floodlights, security lighting, sports court lighting"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Floodlight Installation — Fresno, CA"
        heroSubtitle="Powerful LED floodlight installation for Fresno homes and businesses"
        heroImage="https://public.readdy.ai/ai/img_res/528b78334b6670abbb29235339343bcf.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20high-output%20LED%20floodlight%20fixture%20mounted%20on%20building%20exterior%20with%20bright%20white%20light%20beam%20California%20commercial%20property%20no%20people%20present%20professional%20installation&width=700&height=600&seq=floodlight-fresno-013&orientation=portrait"
        fresnoContent="Illuminate large outdoor areas with professional floodlight installation in Fresno. From residential security lighting in Tower District to commercial building illumination near Highway 99, we install high-output LED floodlights that provide powerful, energy-efficient lighting. Our licensed electricians design systems for security, sports courts, parking areas, and architectural lighting that withstand Central Valley heat. Enjoy bright, reliable outdoor lighting that enhances safety, security, and functionality."
        features={features}
        faqs={[
          {
            question: 'What are the benefits of LED floodlights in Fresno?',
            answer:
              "LED floodlights are ideal for Fresno's climate and offer significant advantages: 70–80% energy savings compared to halogen floodlights, instant-on performance with no warm-up time, 50,000+ hour lifespan (10+ years of nightly use), superior heat tolerance for Fresno summers exceeding 110°F, better light quality and color rendering, and dimmable options for versatility. A typical 150W LED floodlight replaces a 500W halogen while providing equal or better illumination. Most Fresno property owners see ROI within 2–3 years through energy savings.",
          },
          {
            question: 'How much does floodlight installation cost in Fresno?',
            answer:
              'Floodlight installation in Fresno typically costs $300–$800 per fixture depending on size and features. Basic residential LED floodlights (30–50W) start around $250–$400 per fixture including installation. High-output commercial floodlights (100–200W) with motion sensors range from $500–$1,000 per fixture. Sports court lighting systems with multiple fixtures typically cost $3,000–$8,000 depending on court size. We provide free consultations and detailed quotes for all Fresno projects. Energy savings often offset installation costs within 2–3 years.',
          },
          {
            question: 'What size floodlight do I need for my Fresno property?',
            answer:
              'Floodlight size depends on the area you need to illuminate. For Fresno residential properties: 30–50W LED floodlights cover driveways and small backyards (1,000–2,000 sq ft), 75–100W LEDs illuminate larger yards or building facades (2,000–4,000 sq ft), and 150–200W LEDs are for sports courts or commercial areas (4,000–8,000 sq ft). We conduct on-site assessments to determine optimal wattage, quantity, and placement for your specific needs. Proper sizing ensures adequate illumination without wasting energy.',
          },
          {
            question: 'Can you install motion sensor floodlights in Fresno?',
            answer:
              'Yes! Motion sensor floodlights are one of our most popular installations for Fresno properties. Motion sensors provide security by automatically illuminating when activity is detected, save energy by only running when needed (typically 30–60 minutes per night), and offer convenience for arriving home after dark. We install adjustable sensors with customizable detection range (20–70 feet), sensitivity levels, and duration settings. Motion sensor floodlights are perfect for driveways, backyards, and commercial properties throughout Fresno.',
          },
          {
            question: 'Where should floodlights be installed on Fresno properties?',
            answer:
              'For optimal coverage in Fresno, we install floodlights at building corners to illuminate multiple sides, above garage doors for driveway and entryway lighting, on rear walls for backyard and patio illumination, at property perimeters for security coverage, and on poles for sports courts or large commercial areas. Floodlights should be mounted 10–20 feet high and angled to eliminate glare while providing even coverage. We assess your property and recommend strategic placement during consultation.',
          },
          {
            question: 'Do floodlights require permits in Fresno?',
            answer:
              'Yes, floodlight installations in Fresno typically require electrical permits, especially when adding new circuits or modifying existing wiring. Commercial floodlight installations may also require building permits and compliance with light pollution ordinances. We handle all permit applications and inspections with the City of Fresno, ensuring full compliance with California electrical codes. Our licensed C-10 electricians ensure all installations meet Fresno building standards. Proper permitting protects your property value and ensures insurance coverage.',
          },
        ]}
      />
    </>
  );
}
